import {
  Avatar,
  Box,
  Divider,
  Group,
  Menu,
  Select,
  Text,
  UnstyledButton,
  useComputedColorScheme,
  useMantineColorScheme,
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, Cookie, CreditCard, LogOut, Moon, Sparkles, Sun, UserPen } from "lucide-react";
import { useState } from "react";

import { isSessionExpired } from "../../api/errors";
import { useGetTeamSubscription } from "../../api/generated/endpoints/billing/billing";
import { useGetFirstAdTiles } from "../../api/generated/endpoints/generation/generation";
import {
  useGetMe,
  useSignOut,
  useSignOutEverywhere,
} from "../../api/generated/endpoints/identity/identity";
import { useListMyTeams } from "../../api/generated/endpoints/teams/teams";
import type { TeamRole } from "../../api/generated/model";
import { openCookieSettings } from "../consent/consent";
import { ErrorAlert } from "../ErrorAlert";
import { forgetMediaAccess } from "../media/access";
import { managesBilling, ROLE_LABEL } from "../permissions";
import { isManageable } from "../teams/Subscription";
import { UiSwitch } from "../ui-switch/UiSwitch";
import classes from "./shell.module.css";

/** Light or dark, and the word for the switch to the other one (spec 1.5). */
export function useSchemeSwitch() {
  const { setColorScheme } = useMantineColorScheme();
  const dark = useComputedColorScheme("light") === "dark";
  return {
    Icon: dark ? Sun : Moon,
    label: dark ? "Enable light mode" : "Enable dark mode",
    flip: () => setColorScheme(dark ? "light" : "dark"),
  };
}

// The select's last row, which opens the new-team page instead of a team.
const NEW_TEAM = "new";

interface TeamSelectProps {
  teamId: string | undefined;
  /** In Pro, the brand it is on: another team opens Pro's way in, which opens its first brand,
   *  and Create a team opens Pro's New team. */
  proBrandId?: string | undefined;
  onNavigate: () => void;
}

/**
 * The teams the user belongs to (`GET /v1/me/teams`). The selected team is the URL's, so a switch
 * is a navigation: every query key carries the team id, and the team layout swaps the stream.
 */
function TeamSelect({ teamId, proBrandId, onNavigate }: TeamSelectProps) {
  const teams = useListMyTeams();
  const navigate = useNavigate();
  if (teams.isError) return <Text size="xs">Teams did not load</Text>;
  const current = teams.data?.find((team) => team.id.toLowerCase() === teamId?.toLowerCase());
  return (
    <Select
      aria-label="Team"
      mb={30}
      placeholder="Choose a team"
      data={[
        ...(teams.data ?? []).map((team) => ({ value: team.id, label: team.name })),
        { value: NEW_TEAM, label: "Create a team" },
      ]}
      value={current?.id ?? null}
      allowDeselect={false}
      comboboxProps={{ withinPortal: false }}
      leftSection={<span className={classes.teamMark} />}
      onChange={(next) => {
        if (next === null || next === current?.id) return;
        onNavigate();
        // Pro's pages are a brand's: its New team is under the brand Pro is on.
        const inPro = proBrandId !== undefined && teamId !== undefined;
        if (next === NEW_TEAM && inPro) {
          void navigate({
            to: "/teams/$teamId/brands/$brandId/pro/teams/new",
            params: { teamId, brandId: proBrandId },
          });
        } else if (next === NEW_TEAM) {
          void navigate({ to: "/teams/new" });
        } else if (inPro) {
          void navigate({ to: "/teams/$teamId/pro", params: { teamId: next } });
        } else {
          void navigate({ to: "/teams/$teamId", params: { teamId: next } });
        }
      }}
    />
  );
}

interface UserMenuProps {
  /** The URL's team, when the page is a team's. */
  teamId: string | undefined;
  role: TeamRole | undefined;
  /**
   * Pro's header: the brand Pro is on. Its Account settings and team switch stay in Pro, and the
   * menu has no Classic, Simple or Pro, which Pro's header shows at every width. Without it, the
   * Classic pages, and on a phone the pill (spec ui-perf/ui-switch-classic-simple-pro).
   */
  pro?: { brandId: string } | undefined;
}

/**
 * The signed-in user, the team select and the way out. Signing out clears the whole query cache
 * before sign-in shows, so nothing of this session stays on screen (audit frontend 2.11, B.9).
 * Other tabs learn of it on their next request or on focus, through the one ended-session path.
 */
export function UserMenu({ teamId, role, pro }: UserMenuProps) {
  const me = useGetMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const scheme = useSchemeSwitch();
  const [opened, setOpened] = useState(false);
  // Catalog availability offers navigation only; eligibility and redemption stay server-owned.
  const firstAdTiles = useGetFirstAdTiles({ query: { enabled: opened } });
  // The Account page is under a team: the URL's, else the first one, on a page with no team
  // (spec web/account-settings, decision 1).
  const firstTeamId = useListMyTeams().data?.[0]?.id;
  const accountTeamId = teamId ?? firstTeamId;
  // The cancel "in their profile options" (Daniil, 2026-11-05): for those who manage the team's
  // subscription, read when the menu opens, a way to the Team tab, where Manage Subscription is
  // (spec billing/manage-subscription). A team with none to manage gets no item.
  const billing = teamId !== undefined && role !== undefined && managesBilling(role);
  const subscription = useGetTeamSubscription(teamId ?? "", {
    query: { enabled: opened && billing },
  }).data?.subscription;
  const offersSubscription = billing && isManageable(subscription);
  const onSignedOut = () => {
    queryClient.clear();
    forgetMediaAccess();
    void navigate({ to: "/sign-in", replace: true });
  };
  const signOut = useSignOut({ mutation: { onSuccess: onSignedOut } });
  const everywhere = useSignOutEverywhere({ mutation: { onSuccess: onSignedOut } });
  const pending = signOut.isPending || everywhere.isPending;
  // UNAUTHENTICATED means the session is already gone; the shell's handler takes over.
  const failed = [signOut.error, everywhere.error].find(
    (error) => error !== null && !isSessionExpired(error),
  );
  const name = me.data?.name ?? me.data?.email ?? "";
  const roleLabel = role === undefined ? undefined : ROLE_LABEL[role];
  const avatar = <Avatar size="md" color="indigo" name={name} src={me.data?.picture_url ?? null} />;
  // The visible name and role first (WCAG label in name), and what the button opens.
  const triggerName = [name, roleLabel, "account menu"].filter(Boolean).join(", ");
  return (
    <Menu
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={264}
      closeOnItemClick={false}
      transitionProps={{ transition: "pop", duration: 150 }}
    >
      <Menu.Target>
        <UnstyledButton className={classes.userTrigger} aria-label={triggerName}>
          {avatar}
          <Box visibleFrom="sm" className={classes.userText} data-user-text>
            <Text size="sm" fw={600} c="var(--cai-color-text)">
              {name}
            </Text>
            {roleLabel === undefined ? null : (
              <Text size="xs" c="var(--cai-color-text-muted)">
                {roleLabel}
              </Text>
            )}
          </Box>
          <Box visibleFrom="sm" component="span" display="flex" data-user-chevron>
            <ChevronDown
              size={20}
              strokeWidth={1.5}
              className={classes.chevron}
              data-open={opened || undefined}
              aria-hidden
            />
          </Box>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown px={12} py={24}>
        <Box px={12}>
          <Text className={classes.menuLabel}>Account</Text>
          <Group pb={24} wrap="nowrap" align="flex-start">
            {avatar}
            <div>
              <Text size="sm" fw={600} c="var(--cai-color-text)">
                {name}
              </Text>
              <Text size="xs" c="var(--cai-color-text-muted)">
                {me.data?.email}
              </Text>
              {roleLabel === undefined ? null : (
                <Text size="xs" c="var(--cai-color-text-muted)">
                  {roleLabel}
                </Text>
              )}
            </div>
          </Group>
          <Text className={classes.menuLabel}>Team</Text>
          <TeamSelect teamId={teamId} proBrandId={pro?.brandId} onNavigate={() => setOpened(false)} />
          {/* On a phone the header has no room for the pill: Classic, Simple or Pro is here. */}
          {teamId === undefined || pro !== undefined ? null : (
            <Box hiddenFrom="sm" pt="lg">
              <Text className={classes.menuLabel}>Interface</Text>
              <UiSwitch teamId={teamId} onNavigate={() => setOpened(false)} />
            </Box>
          )}
        </Box>
        <Divider my="lg" />
        <Menu.Item
          hiddenFrom="md"
          className={classes.menuItem}
          leftSection={<scheme.Icon size={16} />}
          onClick={scheme.flip}
        >
          {scheme.label}
        </Menu.Item>
        {firstAdTiles.data?.enabled === true ? (
          <Menu.Item
            className={classes.menuItem}
            leftSection={<Sparkles size={16} />}
            onClick={() => {
              setOpened(false);
              void navigate({ to: "/start/free" });
            }}
          >
            Create a free ad
          </Menu.Item>
        ) : null}
        <Menu.Item
          className={classes.menuItem}
          leftSection={<UserPen size={16} />}
          disabled={accountTeamId === undefined}
          onClick={() => {
            if (accountTeamId === undefined) return;
            setOpened(false);
            if (pro !== undefined && teamId !== undefined) {
              void navigate({
                to: "/teams/$teamId/brands/$brandId/pro/settings/account",
                params: { teamId, brandId: pro.brandId },
              });
            } else {
              void navigate({ to: "/teams/$teamId/account", params: { teamId: accountTeamId } });
            }
          }}
        >
          Account settings
        </Menu.Item>
        {offersSubscription && teamId !== undefined ? (
          <Menu.Item
            className={classes.menuItem}
            leftSection={<CreditCard size={16} />}
            onClick={() => {
              setOpened(false);
              if (pro !== undefined) {
                void navigate({
                  to: "/teams/$teamId/brands/$brandId/pro/settings",
                  params: { teamId, brandId: pro.brandId },
                });
              } else {
                void navigate({ to: "/teams/$teamId/members", params: { teamId } });
              }
            }}
          >
            Manage subscription
          </Menu.Item>
        ) : null}
        {/* Live's: the banner again, on its categories, to change or withdraw the answer. */}
        <Menu.Item
          className={classes.menuItem}
          leftSection={<Cookie size={16} />}
          onClick={() => {
            setOpened(false);
            openCookieSettings();
          }}
        >
          Cookie settings
        </Menu.Item>
        <Menu.Item
          className={classes.menuItem}
          leftSection={<LogOut size={16} />}
          disabled={pending}
          onClick={() => signOut.mutate()}
        >
          {signOut.isPending ? "Signing out..." : "Sign out"}
        </Menu.Item>
        <Menu.Item
          className={classes.menuItem}
          leftSection={<LogOut size={16} />}
          disabled={pending}
          onClick={() => everywhere.mutate()}
        >
          {everywhere.isPending ? "Signing out..." : "Sign out of every device"}
        </Menu.Item>
        {failed === undefined ? null : (
          <Box p="xs">
            <ErrorAlert error={failed} />
          </Box>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
